import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { useAtomValue } from 'jotai';
import { scheduleEntriesState, workOrdersState } from '../jotai/atoms';
import styles from './CreateWorkOrderModal.module.css';
import axiosInstance from '../api/axiosInstance';
import MultiSelectSupervisor from './MultiSelectSupervisor';
import { useAuth } from '../context/AuthContext';

function groupConsecutiveDates(entries) {
  if (!entries || entries.length === 0) return [];

  const byTask = entries.reduce((acc, entry) => {
    const taskKey = entry.task.number;
    if (!acc[taskKey]) {
      acc[taskKey] = [];
    }
    acc[taskKey].push(entry);
    return acc;
  }, {});

  const result = [];

  for (const taskKey in byTask) {
    const taskEntries = byTask[taskKey].sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));
    
    if (taskEntries.length === 0) continue;

    let currentSequence = [taskEntries[0]];

    for (let i = 1; i < taskEntries.length; i++) {
      const prevEntry = taskEntries[i - 1];
      const currentEntry = taskEntries[i];
      const prevDate = new Date(prevEntry.scheduledDate);
      const currentDate = new Date(currentEntry.scheduledDate);
      
      const diffTime = currentDate - prevDate;
      const diffDays = diffTime / (1000 * 60 * 60 * 24);

      if (diffDays === 1 && prevEntry.task.id === currentEntry.task.id) {
        currentSequence.push(currentEntry);
      } else {
        if (currentSequence.length > 1) {
          result.push({
            isGroup: true,
            id: `group-${currentSequence[0].task.id}-${currentSequence[0].scheduledDate}`,
            task: currentSequence[0].task,
            startDate: currentSequence[0].scheduledDate,
            endDate: currentSequence[currentSequence.length - 1].scheduledDate,
            entries: currentSequence,
            assignedManagers: currentSequence[0].assignedManagers,
            assignedAssistants: currentSequence[0].assignedAssistants,
          });
        } else {
          result.push(currentSequence[0]);
        }
        currentSequence = [currentEntry];
      }
    }

    if (currentSequence.length > 1) {
      result.push({
        isGroup: true,
        id: `group-${currentSequence[0].task.id}-${currentSequence[0].scheduledDate}`,
        task: currentSequence[0].task,
        startDate: currentSequence[0].scheduledDate,
        endDate: currentSequence[currentSequence.length - 1].scheduledDate,
        entries: currentSequence,
        assignedManagers: currentSequence[0].assignedManagers,
        assignedAssistants: currentSequence[0].assignedAssistants,
      });
    } else if (currentSequence.length === 1) {
      result.push(currentSequence[0]);
    }
  }

  return result.sort((a, b) => {
    const dateA = new Date(a.isGroup ? a.startDate : a.scheduledDate);
    const dateB = new Date(b.isGroup ? b.startDate : b.scheduledDate);
    return dateA - dateB;
  });
}

function CreateWorkOrderModal({ isOpen, onClose, onCreateWorkOrder, initialSelectedEntry }) {
  const [workType, setWorkType] = useState('');
  const [workOrderTitle, setWorkOrderTitle] = useState('');
  const [noticeDetails, setNoticeDetails] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [otherDetails, setOtherDetails] = useState('');
  const [otherTitle, setOtherTitle] = useState('');
  const [supervisors, setSupervisors] = useState([]);
  const [principalSupervisors, setPrincipalSupervisors] = useState([]);
  const [assistantSupervisors, setAssistantSupervisors] = useState([]);

  const scheduleEntries = useAtomValue(scheduleEntriesState);
  const workOrderColumns = useAtomValue(workOrdersState);

  const [selectedScheduleEntries, setSelectedScheduleEntries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    if (initialSelectedEntry) {
      setWorkType('배치');
      setSelectedScheduleEntries(Array.isArray(initialSelectedEntry) ? initialSelectedEntry : [initialSelectedEntry]);
    }
  }, [initialSelectedEntry]);

  useEffect(() => {
    setSelectedScheduleEntries([]);
    setNoticeDetails('');
    setReportDetails('');
    setOtherDetails('');
    setOtherTitle('');
    setSearchTerm('');
    setPrincipalSupervisors([]);
    setAssistantSupervisors([]);
  }, [workType]);

  useEffect(() => {
    const fetchSupervisors = async () => {
      try {
        const response = await axiosInstance.get('/supervisors');
        setSupervisors(response.data);
      } catch (error) {
        console.error('Error fetching supervisors:', error);
      }
    };

    if (workType === '시공통보') {
      fetchSupervisors();
    }
  }, [workType]);

  useEffect(() => {
    if (workType === '배치') {
      if (selectedScheduleEntries.length > 0) {
        const firstEntry = selectedScheduleEntries[0];
        const sortedDates = selectedScheduleEntries.map(e => e.scheduledDate).sort();
        const startDate = sortedDates[0];
        const endDate = sortedDates[sortedDates.length - 1];
        const dateString = startDate === endDate ? startDate : `${startDate} ~ ${endDate}`;
        setWorkOrderTitle(`[${firstEntry.task.number}] ${firstEntry.task.name} ${dateString} 배치`);
      } else {
        setWorkOrderTitle('');
      }
    } else if (workType) {
      setWorkOrderTitle(workType);
    } else {
      setWorkOrderTitle('');
    }
  }, [workType, selectedScheduleEntries]);

  if (!isOpen) return null;

  const handleScheduleEntrySelect = (clickedEntry) => {
    const isAlreadySelected = selectedScheduleEntries.length === 1 && selectedScheduleEntries[0].id === clickedEntry.id;

    if (isAlreadySelected) {
      setSelectedScheduleEntries([]);
    } else {
      setSelectedScheduleEntries([clickedEntry]);
    }
  };

  const handleGroupedEntrySelect = (group) => {
    const allIdsInGroup = group.entries.map(e => e.id);
    const isGroupSelected = selectedScheduleEntries.length === allIdsInGroup.length && allIdsInGroup.every(id => selectedScheduleEntries.some(e => e.id === id));

    if (isGroupSelected) {
      setSelectedScheduleEntries([]);
    } else {
      setSelectedScheduleEntries(group.entries);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!workType) {
      Swal.fire({ icon: 'warning', title: '입력 오류', text: '작업 종류를 선택해주세요.' });
      return;
    }

    if (!workOrderTitle.trim()) {
      Swal.fire({ icon: 'warning', title: '입력 오류', text: "'배치'의 경우, 먼저 배치된 감리원 항목을 선택해야 합니다." });
      return;
    }

    let details = {};
    switch (workType) {
      case '배치':
        if (selectedScheduleEntries.length === 0) {
          Swal.fire({ icon: 'warning', title: '입력 오류', text: '배치된 감리원 항목을 선택해주세요.' });
          return;
        }
        details = {
          taskNumber: selectedScheduleEntries[0].task.number,
          taskRemarks: selectedScheduleEntries[0].task.remarks || '',
          assignments: selectedScheduleEntries.map(entry => ({
            assignmentDate: entry.scheduledDate,
            taskId: entry.task.id,
            taskName: entry.task.name,
            assignedManagers: entry.assignedManagers,
            assignedAssistants: entry.assignedAssistants,
          })),
        };
        break;
      case '시공통보':
        if (!noticeDetails.trim()) {
          Swal.fire({ icon: 'warning', title: '입력 오류', text: '시공통보 상세 내용을 입력해주세요.' });
          return;
        }
        if (principalSupervisors.length === 0) {
          Swal.fire({ icon: 'warning', title: '입력 오류', text: '책임감리원을 선택해주세요.' });
          return;
        }
        details = { 
          noticeDetails, 
          principalSupervisor: principalSupervisors.join(','), 
          assistantSupervisor: assistantSupervisors.join(',') 
        };
        break;
      case '기타':
        if (!otherTitle.trim()) {
          Swal.fire({ icon: 'warning', title: '입력 오류', text: '기타 작업 제목을 입력해주세요.' });
          return;
        }
        if (!otherDetails.trim()) {
          Swal.fire({ icon: 'warning', title: '입력 오류', text: '기타 상세 내용을 입력해주세요.' });
          return;
        }
        details = { otherTitle, otherDetails };
        break;
      default:
        break;
    }

    try {
      const detailsJsonString = JSON.stringify(details);
      const response = await axiosInstance.post('/work-orders', {
        title: workOrderTitle,
        type: workType,
        details: detailsJsonString,
        status: 'todo',
      });
      const newWorkOrder = response.data;

      if (workType === '배치') {
        for (const entry of selectedScheduleEntries) {
          try {
            await axiosInstance.put(`/schedule-entries/${entry.task.id}/update-deployment-status`, null, {
              params: {
                scheduledDate: entry.scheduledDate,
                newStatus: '배치지시'
              }
            });
          } catch (statusError) {
            console.error(`Failed to update deployment status for entry ${entry.id}:`, statusError);
          }
        }
      }

      await Swal.fire('성공', '작업 지시가 성공적으로 생성되었습니다.', 'success');
      onCreateWorkOrder(newWorkOrder);
    } catch (error) {
      console.error('Error creating work order:', error);
      Swal.fire('오류', '작업 지시 생성 중 오류가 발생했습니다.', 'error');
    }

    setWorkType('');
    onClose();
  };

  const renderFormFields = () => {
    switch (workType) {
      case '배치':
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const allWorkOrderItems = Object.values(workOrderColumns).flatMap(column => column.items || []).map(item => {
          if (item.details && typeof item.details === 'string') {
            try {
              return { ...item, details: JSON.parse(item.details) };
            } catch (e) {
              console.error("Failed to parse details JSON for existing work order:", item.id, e);
              return { ...item, details: {} };
            }
          }
          return item;
        });

        const existingWorkOrderKeys = new Set(
          allWorkOrderItems
            .filter(wo => wo.type === '배치' && wo.details.assignments)
            .flatMap(wo => wo.details.assignments)
            .map(as => `${as.taskId}-${as.assignmentDate}`)
        );

        const availableEntries = scheduleEntries.filter(entry => {
          const parts = entry.scheduledDate.split('-');
          const entryDate = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
          entryDate.setHours(0, 0, 0, 0);
          const workOrderKey = `${entry.task.id}-${entry.scheduledDate}`;

          const matchesSearch = 
            entry.task.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            entry.task.number.toLowerCase().includes(searchTerm.toLowerCase());

          const isFutureOrToday = entryDate >= today;
          const isNotExisting = !existingWorkOrderKeys.has(workOrderKey);
          const isNotDeployed = entry.deploymentStatus !== '배치완료';

          return isFutureOrToday && isNotExisting && isNotDeployed && matchesSearch;
        });

        const groupedAndAvailableEntries = groupConsecutiveDates(availableEntries);

        return (
          <div className={styles['form-section']}>
            <div className={styles['form-group']}>
              <h3>{workType}</h3>
              <input
                id="task-search"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="공사명 또는 번호로 검색"
              />
              <br/>
            </div>
            <div className={styles['form-group']}>
              <label>배치할 공사 선택</label>
              <ul className={styles['schedule-entry-list']}>
                {groupedAndAvailableEntries.length > 0 ? (
                  groupedAndAvailableEntries.map(item => {
                    if (item.isGroup) {
                      const allInGroupSelected = item.entries.every(e => selectedScheduleEntries.some(se => se.id === e.id));
                      return (
                        <li
                          key={item.id}
                          className={allInGroupSelected ? styles.selected : ''}
                          onClick={() => handleGroupedEntrySelect(item)}
                        >
                          <span style={{ color: allInGroupSelected ? 'inherit' : 'red', fontWeight: 'bold' }}>{`${item.startDate} ~ ${item.endDate}`}</span>
                          {`: [${item.task.number}] ${item.task.name} (책임: ${item.assignedManagers}, 보조: ${item.assignedAssistants || '없음'})`}
                        </li>
                      );
                    } else {
                      const isSelected = selectedScheduleEntries.some(e => e.id === item.id);
                      return (
                        <li
                          key={item.id}
                          className={isSelected ? styles.selected : ''}
                          onClick={() => handleScheduleEntrySelect(item)}
                        >
                          <span style={{ color: isSelected ? 'inherit' : 'red', fontWeight: 'bold' }}>{item.scheduledDate}</span>
                          {`: [${item.task.number}] ${item.task.name} (책임: ${item.assignedManagers}, 보조: ${item.assignedAssistants || '없음'})`}
                        </li>
                      );
                    }
                  })
                ) : (
                  <li style={{ cursor: 'default', color: '#888' }}>검색 결과가 없거나, 오늘 이후로 생성할 수 있는 배치 정보가 없습니다.</li>
                )}
              </ul>
            </div>
            {selectedScheduleEntries.length > 0 && (
              <div className={styles['form-group']}>
                <label>선택된 배치 정보</label>
                <div className={styles['selected-entries-display']}>
                  {selectedScheduleEntries.map(entry => (
                    <p key={entry.id}>{`${entry.scheduledDate}: [${entry.task.number}] ${entry.task.name}`}</p>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      case '시공통보':
        return (
          <div className={styles['form-section']}>
            <div className={styles['form-group']}>
              <h3>{workType}</h3>
              <textarea
                id="notice-details"
                value={noticeDetails}
                onChange={(e) => setNoticeDetails(e.target.value)}
                placeholder="시공통보 공사명을 입력해주세요."
                rows="3"
              />
            </div>
            <div className={styles['form-grid-2-col']}>
              <div className={styles['form-group']}>
                <label>책임감리원</label>
                <MultiSelectSupervisor
                  supervisors={supervisors}
                  selectedSupervisors={principalSupervisors}
                  onSelectionChange={setPrincipalSupervisors}
                  placeholder="책임감리원을 선택하세요"
                  isManager={true}
                  otherSelectedSupervisors={assistantSupervisors}
                />
              </div>
              <div className={styles['form-group']}>
                <label>보조감리원</label>
                <MultiSelectSupervisor
                  supervisors={supervisors}
                  selectedSupervisors={assistantSupervisors}
                  onSelectionChange={setAssistantSupervisors}
                  placeholder="보조감리원을 선택하세요"
                  isManager={false}
                  otherSelectedSupervisors={principalSupervisors}
                />
              </div>
            </div>
          </div>
        );
      case '기타':
        return (
          <div className={styles['form-section']}>
            <div className={styles['form-group']}>
              <h3>{workType}</h3>
              <input
                id="other-title"
                type="text"
                value={otherTitle}
                onChange={(e) => setOtherTitle(e.target.value)}
                placeholder="기타 작업의 제목을 입력하세요."
              />
            </div>
            <div className={styles['form-group']}>
              <label htmlFor="other-details">상세 내용</label>
              <textarea
                id="other-details"
                value={otherDetails}
                onChange={(e) => setOtherDetails(e.target.value)}
                placeholder="기타 작업 관련 상세 내용을 입력하세요."
                rows="5"
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal-content ${styles['create-work-order-modal']}`} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="modal-close-button">×</button>
        {!workType ? (
          <div className={styles['work-type-selection']}>
            <h4>1. 작업 종류를 선택하세요</h4>
            <div className={styles['work-type-buttons']}>
              {user && ['ADMIN'].includes(user.role) &&  (
                <button className={styles['work-type-btn']} onClick={() => setWorkType('배치')}>배치</button>
              )}
              {user && ['ADMIN'].includes(user.role) &&  (
                <button className={styles['work-type-btn']} onClick={() => setWorkType('시공통보')}>시공통보</button>
              )}
              {user && ['ADMIN', 'MIDADMIN'].includes(user.role) &&  (
                <button className={styles['work-type-btn']} onClick={() => setWorkType('기타')}>기타</button>
              )}
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className={styles['form-header']}>
              <button type="button" className={styles['back-button']} onClick={() => setWorkType('')}>종류 변경</button>
              {/* <h3>{workType}</h3> */}
              <div></div> 
            </div>

            <div className={styles['form-content']}>
              {renderFormFields()}
            </div>

            <div className={styles['form-actions']}>
              <button type="submit" className={styles['submit-button']}>생성하기</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default CreateWorkOrderModal;